import axios from "axios";
import type { AxiosResponse } from "axios";
import { ApiError, OpenAPI } from "../../.generated/api-client";
import { clearAdminImpersonationSession } from "./impersonationSession";

const DEFAULT_API_BASE = "";
const ADMIN_TOKEN_STORAGE_KEY = "ember_admin_token";
const REFRESHED_JWT_HEADER = "x-refreshed-jwt";
const ROLE_CHANGED_CODE = "ROLE_CHANGED";
const UNBOUND_CODE = "UNBOUND";

export const ADMIN_AUTH_SESSION_EVENT = "ember-admin-auth-session";

export type AdminAuthSessionReason =
  | "unauthorized"
  | "role_changed"
  | "unbound";

export type AdminAuthSessionDetail = {
  reason: AdminAuthSessionReason;
  status: 401 | 403;
  code: string | null;
  url: string | null;
};

type AdminAuthHttpResponse = {
  status?: number;
  headers?: unknown;
  data?: unknown;
  body?: unknown;
  config?: { url?: string };
  url?: string;
};

let adminReconnectPending = false;

OpenAPI.BASE = import.meta.env.VITE_EMBER_API_BASE ?? DEFAULT_API_BASE;
OpenAPI.TOKEN = () => {
  const token = localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
  return Promise.resolve(token ?? "");
};

/**
 * 返回当前生效的后端 API base URL。
 *
 * 复用 generated client 的 `OpenAPI.BASE`（本文件已用 `VITE_EMBER_API_BASE ?? ''`
 * 初始化），保证错误上报等旁路请求与主链路指向同一后端，不硬编码地址、不引入
 * localhost 兜底（见 DEC-0006 / PIT-003）。空字符串表示同源相对路径。
 */
export function getApiBase(): string {
  return typeof OpenAPI.BASE === "string" ? OpenAPI.BASE : DEFAULT_API_BASE;
}

export function getAdminToken(): string {
  return localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) ?? "";
}

export function setAdminToken(token: string): void {
  localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);
  adminReconnectPending = false;
}

export function clearAdminToken(): void {
  localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
}

export function resetAdminAuthReconnectState(): void {
  adminReconnectPending = false;
}

function stringHeaderValue(value: unknown): string | null {
  if (Array.isArray(value)) {
    return stringHeaderValue(value[0]);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number") {
    return String(value);
  }
  return null;
}

function responseHeaderValue(headers: unknown, name: string): string | null {
  if (!headers || typeof headers !== "object") {
    return null;
  }

  const lowerName = name.toLowerCase();
  const maybeGetter = (headers as { get?: unknown }).get;
  if (typeof maybeGetter === "function") {
    const fromGetter =
      maybeGetter.call(headers, name) ?? maybeGetter.call(headers, lowerName);
    const value = stringHeaderValue(fromGetter);
    if (value) return value;
  }

  for (const [key, value] of Object.entries(
    headers as Record<string, unknown>,
  )) {
    if (key.toLowerCase() === lowerName) {
      return stringHeaderValue(value);
    }
  }
  return null;
}

export function apiErrorCodeFromBody(body: unknown): string | null {
  const root = asRecord(body);
  const error = asRecord(root.error ?? root.detail ?? root);
  const code = error.code;
  return typeof code === "string" && code.length > 0 ? code : null;
}

function dispatchAdminAuthSessionEvent(detail: AdminAuthSessionDetail): void {
  if (typeof window === "undefined") {
    return;
  }
  if (typeof CustomEvent === "function") {
    window.dispatchEvent(
      new CustomEvent<AdminAuthSessionDetail>(ADMIN_AUTH_SESSION_EVENT, {
        detail,
      }),
    );
    return;
  }
  const event = {
    type: ADMIN_AUTH_SESSION_EVENT,
    detail,
  } as CustomEvent<AdminAuthSessionDetail>;
  window.dispatchEvent(event);
}

function requestAdminReconnect(detail: AdminAuthSessionDetail): void {
  if (adminReconnectPending) {
    return;
  }
  adminReconnectPending = true;
  clearAdminToken();
  clearAdminImpersonationSession();
  dispatchAdminAuthSessionEvent(detail);
}

function adminReconnectReason(
  status: number | undefined,
  code: string | null,
): AdminAuthSessionReason | null {
  if (status === 401) {
    return "unauthorized";
  }
  if (status !== 403) {
    return null;
  }
  if (code === ROLE_CHANGED_CODE) {
    return "role_changed";
  }
  if (code === UNBOUND_CODE) {
    return "unbound";
  }
  return null;
}

export function handleAdminAuthResponse(response: AdminAuthHttpResponse): void {
  const refreshedJwt = responseHeaderValue(
    response.headers,
    REFRESHED_JWT_HEADER,
  );
  if (refreshedJwt) {
    setAdminToken(refreshedJwt);
  }

  const status = response.status;
  if (status !== 401 && status !== 403) {
    return;
  }

  const code = apiErrorCodeFromBody(response.data ?? response.body);
  const reason = adminReconnectReason(status, code);
  if (reason) {
    requestAdminReconnect({
      reason,
      status,
      code,
      url: response.config?.url ?? response.url ?? null,
    });
  }
}

function responseFromAxiosError(error: unknown): AdminAuthHttpResponse | null {
  if (!error || typeof error !== "object") {
    return null;
  }
  const response = (error as { response?: AdminAuthHttpResponse }).response;
  return response && typeof response === "object" ? response : null;
}

axios.interceptors.response.use(
  (response: AxiosResponse<unknown>) => {
    handleAdminAuthResponse(response);
    return response;
  },
  (error: unknown) => {
    const response = responseFromAxiosError(error);
    if (response) {
      handleAdminAuthResponse(response);
    }
    return Promise.reject(error);
  },
);

export async function adminAuthFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = getAdminToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(input, { ...init, headers });
  const refreshedJwt = response.headers.get(REFRESHED_JWT_HEADER);
  if (refreshedJwt && refreshedJwt.trim()) {
    setAdminToken(refreshedJwt.trim());
  }

  if (response.status === 401 || response.status === 403) {
    const code = await response
      .clone()
      .json()
      .then(apiErrorCodeFromBody)
      .catch(() => null);
    const reason = adminReconnectReason(response.status, code);
    if (reason) {
      requestAdminReconnect({
        reason,
        status: response.status,
        code,
        url: typeof input === "string" ? input : input.toString(),
      });
    }
  }

  return response;
}

type ValidationIssue = {
  loc: Array<string | number>;
  type: string;
};

const VALIDATION_LOCATION_BOUNDARIES = new Set([
  "body",
  "query",
  "path",
  "header",
  "cookie",
  "response",
]);
const REDACTED_VALIDATION_LOCATION = "[dynamic location hidden]";

const VALIDATION_FIELD_LABELS: Record<string, string> = {
  assignments: "排班内容",
  shift_template_ids: "班次",
  short_name: "格内简称",
  employee_id: "员工",
  employee_ids: "员工",
  work_date: "排班日期",
  week_start: "排班周",
  store_id: "门店",
  store_ids: "门店",
  full_name: "姓名",
  candidate_full_name: "候选人姓名",
  phone: "手机号",
  candidate_phone: "候选人手机号",
  contact_phone: "联系电话",
  position: "岗位",
  operation_type: "操作类型",
  invoice_type: "发票类型",
  task_type: "任务类型",
  store_type: "门店类型",
  scope_type: "范围类型",
  employment_type: "雇佣类型",
  hire_date: "入职日期",
  offboard_date: "离职日期",
  initial_store_assignments: "名下门店",
  manager_employee_id: "直属上级",
  successor_employee_id: "接任人",
  start_time: "开始时间",
  end_time: "结束时间",
  in_time: "上班时间",
  out_time: "下班时间",
  date_range: "日期范围",
  month: "月份",
  count_date: "盘点日期",
  punch_date: "打卡日期",
  reason: "原因",
  reason_text: "原因说明",
  change_reason: "变更原因",
  remark: "备注",
  remarks: "备注",
  note: "说明",
  notes: "说明",
  title: "标题",
  name: "名称",
  item_name: "项目名称",
  items: "项目",
  categories: "分类",
  quantity: "数量",
  amount: "金额",
  base_amount: "基础金额",
  hourly_rate: "时薪",
  salary_base_amount: "基础工资",
  salary_daily_rate: "日薪",
  probation_months: "试用期月数",
  staff_headcount: "员工人数",
  seat_count: "座位数",
  limit: "数量上限",
  page: "页码",
};

function validationIssuesFromBody(body: UnknownRecord): ValidationIssue[] {
  const detail = body.detail;
  if (!Array.isArray(detail)) {
    return [];
  }
  return detail.flatMap((raw) => {
    const issue = asRecord(raw);
    if (!Array.isArray(issue.loc) || typeof issue.type !== "string") {
      return [];
    }
    const loc = issue.loc.filter(
      (part): part is string | number =>
        (typeof part === "string" || typeof part === "number") &&
        typeof part !== "boolean",
    );
    return loc.length > 0 ? [{ loc, type: issue.type }] : [];
  });
}

function inferredValidationFieldLabel(field: string): string | null {
  const normalized = field.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  if (VALIDATION_FIELD_LABELS[normalized]) {
    return VALIDATION_FIELD_LABELS[normalized];
  }
  if (normalized.includes("phone")) return "手机号";
  if (normalized.includes("email")) return "邮箱";
  if (normalized.includes("date") || normalized.endsWith("_on")) return "日期";
  if (normalized.includes("time") || normalized.endsWith("_at")) return "时间";
  if (normalized.includes("amount") || normalized.includes("price")) return "金额";
  if (normalized.includes("quantity") || normalized.endsWith("_qty")) return "数量";
  if (normalized.includes("name")) return "名称";
  if (normalized.includes("reason")) return "原因";
  if (normalized.includes("remark") || normalized.includes("note")) return "备注";
  return null;
}

function validationIssueLocation(issue: ValidationIssue): string {
  let label = "";
  for (const part of issue.loc) {
    if (typeof part === "number" && Number.isInteger(part) && part >= 0) {
      label += `第${part + 1}项`;
      continue;
    }
    if (
      typeof part !== "string" ||
      VALIDATION_LOCATION_BOUNDARIES.has(part) ||
      part === REDACTED_VALIDATION_LOCATION
    ) {
      continue;
    }
    const fieldLabel = inferredValidationFieldLabel(part);
    if (fieldLabel) {
      label += `${label ? "的" : ""}${fieldLabel}`;
    }
  }
  return label || "表单中的相关输入项";
}

function validationIssueMessage(issue: ValidationIssue): string {
  const location = validationIssueLocation(issue);
  const type = issue.type;
  if (type === "extra_forbidden") {
    return "表单包含无法识别的内容，请刷新页面后重新填写";
  }
  if (type === "missing") return `请填写${location}`;
  if (type === "list_type" || type === "tuple_type" || type === "set_type") {
    return `${location}格式不正确，请重新选择`;
  }
  if (
    type.startsWith("int_") ||
    type.startsWith("float_") ||
    type.startsWith("decimal_") ||
    type === "finite_number"
  ) {
    return `${location}需要填写有效数字，请修改后重试`;
  }
  if (type === "bool_type" || type === "bool_parsing") {
    return `${location}选项不正确，请重新选择`;
  }
  if (type === "string_type") return `${location}需要填写文字，请修改后重试`;
  if (type === "string_too_short" || type === "too_short") {
    return `${location}内容太短，请补充完整`;
  }
  if (type === "string_too_long" || type === "too_long") {
    return `${location}内容太长，请删减后重试`;
  }
  if (type === "greater_than" || type === "greater_than_equal") {
    return `${location}数值太小，请调大后重试`;
  }
  if (type === "less_than" || type === "less_than_equal") {
    return `${location}数值太大，请调小后重试`;
  }
  if (type === "literal_error" || type === "enum") {
    return `${location}选项不可用，请重新选择`;
  }
  if (
    type.includes("pattern") ||
    type.includes("parsing") ||
    type.startsWith("date_") ||
    type.startsWith("time_") ||
    type.startsWith("datetime_") ||
    type === "url_type"
  ) {
    return `${location}格式不正确，请按页面要求修改`;
  }
  return `${location}填写有误，请检查后重试`;
}

function validationErrorMessage(body: UnknownRecord): string | null {
  const issues = validationIssuesFromBody(body);
  if (issues.length === 0) {
    return null;
  }
  const visible = issues.slice(0, 3).map(validationIssueMessage);
  if (issues.length > visible.length) {
    visible.push(`另外还有 ${issues.length - visible.length} 项填写有误，请一并检查`);
  }
  return `${visible.join("；")}。`;
}

export function apiErrorMessage(error: unknown): string {
  const apiError = apiErrorLike(error);
  const axiosResponse = responseFromAxiosError(error);
  const body = apiErrorBody(error);
  if (apiError || axiosResponse || Object.keys(body).length > 0) {
    const errorBody = asRecord(body.error ?? body);
    const backendMessage =
      typeof errorBody.message === "string"
        ? errorBody.message.trim()
        : typeof body.detail === "string"
          ? body.detail.trim()
          : "";
    const status =
      typeof apiError?.status === "number"
        ? apiError.status
        : typeof axiosResponse?.status === "number"
          ? axiosResponse.status
          : 0;
    const fallbackMessage =
      typeof apiError?.message === "string"
        ? apiError.message
        : error instanceof Error
          ? error.message
          : "";
    const code = apiErrorCodeFromBody(body);

    if (status === 401) {
      return "当前电脑端没有有效账号连接，请先连接账号后重试。";
    }
    if (status === 403 && code === UNBOUND_CODE) {
      return "当前账号尚未完成微信绑定，请联系管理员生成绑定码；绑定后重新扫码登录。";
    }
    if (status === 403) {
      return "当前账号没有访问该范围的权限。";
    }
    const validationMessage = validationErrorMessage(body);
    if (validationMessage) {
      return validationMessage;
    }
    if (backendMessage && status < 500) {
      return backendMessage;
    }
    if (status >= 500) {
      return "服务暂时不可用，请稍后重试。";
    }
    return fallbackMessage || (status ? `请求失败 (${status})` : "请求失败，请稍后重试。");
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "请求失败，请稍后重试。";
}

export type UnknownRecord = Record<string, unknown>;

type ApiErrorLike = {
  body?: unknown;
  message?: unknown;
  status?: unknown;
};

function apiErrorLike(error: unknown): ApiErrorLike | null {
  if (error instanceof ApiError) {
    return error;
  }
  if (!error || typeof error !== "object" || Array.isArray(error)) {
    return null;
  }
  const candidate = error as ApiErrorLike;
  if (typeof candidate.status === "number" || candidate.body !== undefined) {
    return candidate;
  }
  if (
    typeof candidate.message === "string" &&
    candidate.message.includes("Generic Error:")
  ) {
    return candidate;
  }
  return null;
}

function parseGenericErrorBody(message: unknown): UnknownRecord {
  if (typeof message !== "string") {
    return {};
  }
  const marker = "body: ";
  const markerIndex = message.indexOf(marker);
  if (markerIndex === -1) {
    return {};
  }
  const rawBody = message.slice(markerIndex + marker.length).trim();
  if (!rawBody.startsWith("{") && !rawBody.startsWith("[")) {
    return {};
  }
  try {
    return asRecord(JSON.parse(rawBody));
  } catch {
    return {};
  }
}

export function apiErrorBody(error: unknown): UnknownRecord {
  const axiosResponse = responseFromAxiosError(error);
  const responseBody = asRecord(axiosResponse?.data ?? axiosResponse?.body);
  if (Object.keys(responseBody).length > 0) {
    return responseBody;
  }
  const apiError = apiErrorLike(error);
  if (apiError) {
    const body = asRecord(apiError.body);
    if (Object.keys(body).length > 0) {
      return body;
    }
    const genericBody = parseGenericErrorBody(apiError.message);
    if (Object.keys(genericBody).length > 0) {
      return genericBody;
    }
  }
  const directBody = asRecord(error);
  if (directBody.detail !== undefined || directBody.error !== undefined) {
    return directBody;
  }
  return {};
}

export function asRecord(value: unknown): UnknownRecord {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as UnknownRecord;
  }
  return {};
}

export function asArray(value: unknown): UnknownRecord[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(asRecord);
}

export function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === "string");
}

export function asNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (item): item is number => typeof item === "number" && Number.isFinite(item),
  );
}

export function stringValue(value: unknown, fallback = "—"): string {
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

export function numberValue(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
